import * as React from "react";
import { cva } from "class-variance-authority";
import { TooltipShort } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * IconButton — small icon-only button with optional tooltip on hover.
 *
 * Replaces the legacy `ActionButton` from `pages/visitors/index.jsx`.
 * Tooltip appears automatically when `label` is provided.
 *
 * @example
 *   <IconButton label="Bearbeiten" onClick={...}>
 *     <EditIcon />
 *   </IconButton>
 *
 *   <IconButton label="Löschen" variant="danger" onClick={...}>
 *     <DeleteIcon />
 *   </IconButton>
 */
const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-btn border border-transparent transition-all duration-100 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default: "text-dim hover:bg-[#f4f4f5] hover:border-gray hover:text-foreground",
        danger: "text-destructive hover:bg-[#fef2f2] hover:border-[#fecaca] hover:text-destructive",
        primary: "text-foreground hover:bg-[#fefce8] hover:border-primary",
      },
      size: {
        sm: "w-[3rem] h-[3rem] [&_svg]:w-[1.4rem] [&_svg]:h-[1.4rem]",
        md: "w-[3.6rem] h-[3.6rem] [&_svg]:w-[1.8rem] [&_svg]:h-[1.8rem]",
        lg: "w-[4.4rem] h-[4.4rem] [&_svg]:w-[2.2rem] [&_svg]:h-[2.2rem]",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

const IconButton = React.forwardRef(
  (
    { className, variant, size, label, type = "button", onClick, children, ...props },
    ref,
  ) => {
    const button = (
      <button
        ref={ref}
        type={type}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        aria-label={label}
        className={cn(iconButtonVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </button>
    );
    if (!label) return button;
    return <TooltipShort label={label}>{button}</TooltipShort>;
  },
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
